import { useId, useRef, useEffect, type InputHTMLAttributes, type ReactNode } from 'react';
import { Check, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

// Re-authored from ../design-system/components/forms/Checkbox.jsx.
// Uses a visually-hidden native <input type="checkbox" class="peer sr-only"> so
// Tailwind peer-* variants (peer-checked, peer-focus-visible, peer-indeterminate,
// peer-disabled) can drive the styled box sibling. The lucide glyph (Check or
// Minus) starts opacity-0 and is revealed via peer-checked:[&_svg]:opacity-100 /
// peer-indeterminate:[&_svg]:opacity-100 on the box span.
//
// NOTE: ref is NOT forwarded. An internal ref drives .indeterminate on the DOM
// input — merging a caller ref with the internal ref would require useImperativeHandle
// or a merge helper that adds noise with no test need. The test only pins the
// DOM .indeterminate property, which the internal ref satisfies.

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: ReactNode;
  description?: ReactNode;
  indeterminate?: boolean;
}

export function Checkbox({
  label,
  description,
  indeterminate = false,
  id,
  className,
  ...rest
}: CheckboxProps) {
  const autoId = useId();
  const fieldId = id ?? (label ? autoId : undefined);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = !!indeterminate;
    }
  }, [indeterminate]);

  return (
    <label
      htmlFor={fieldId}
      className={cn(
        'inline-flex items-start gap-3 cursor-pointer select-none font-sans',
        // disabled dim is owned by the label via has-[:disabled] (peer-disabled can't reach text descendants).
        'has-[:disabled]:opacity-50 has-[:disabled]:cursor-not-allowed',
        className,
      )}
    >
      {/* visually-hidden native input — the "peer" */}
      <input
        ref={inputRef}
        id={fieldId}
        type="checkbox"
        className="peer sr-only"
        {...rest}
      />

      {/* styled box — peer sibling, so peer-* variants work */}
      <span
        className={cn(
          // base box
          'flex-none h-5 w-5 mt-px flex items-center justify-center',
          'border-[1.5px] border-border bg-card rounded-[6px]',
          'transition-[background-color,border-color,box-shadow]',
          // checked: navy fill
          'peer-checked:bg-primary peer-checked:border-primary',
          // indeterminate: navy fill
          'peer-indeterminate:bg-primary peer-indeterminate:border-primary',
          // focus-visible: gold ring
          'peer-focus-visible:ring-[3px] peer-focus-visible:ring-[var(--focus-glow)] peer-focus-visible:border-accent',
          // disabled: label owns the dim via has-[:disabled]; no per-element class needed here
          // glyph visibility: svg starts hidden, revealed when checked OR indeterminate
          'peer-checked:[&_svg]:opacity-100 peer-checked:[&_svg]:scale-100',
          'peer-indeterminate:[&_svg]:opacity-100 peer-indeterminate:[&_svg]:scale-100',
        )}
      >
        {/* glyph: Minus when indeterminate, Check otherwise */}
        {indeterminate ? (
          <Minus
            className="h-3.5 w-3.5 text-white opacity-0 scale-75 transition-[opacity,transform]"
            strokeWidth={2.5}
            aria-hidden="true"
          />
        ) : (
          <Check
            className="h-3.5 w-3.5 text-white opacity-0 scale-75 transition-[opacity,transform]"
            strokeWidth={2.5}
            aria-hidden="true"
          />
        )}
      </span>

      {/* optional label + description column */}
      {(label != null || description != null) && (
        <span className="flex flex-col gap-0.5">
          {label != null && (
            <span className="text-base text-foreground leading-snug">
              {label}
            </span>
          )}
          {description != null && (
            <span className="text-xs text-muted">{description}</span>
          )}
        </span>
      )}
    </label>
  );
}
